import { useEffect, useState } from 'react';

/**
 * Whether the page is in the foreground.
 *
 * Load-bearing, not cosmetic. This app has no background execution of any kind
 * (SPEC §9): switch away, lock the screen, or let the display sleep, and iOS
 * suspends the timers that drive the cycle. The loop therefore has to know the
 * difference between "waiting for the next tick" and "frozen", because those
 * look identical from inside a `setTimeout` that simply never fires.
 */
export function useVisibility(): boolean {
  const [visible, setVisible] = useState(() => document.visibilityState !== 'hidden');

  useEffect(() => {
    const update = () => setVisible(document.visibilityState !== 'hidden');
    document.addEventListener('visibilitychange', update);
    // iOS fires `pageshow` when a page comes back from the back/forward cache,
    // a case `visibilitychange` alone can miss.
    window.addEventListener('pageshow', update);
    window.addEventListener('focus', update);
    return () => {
      document.removeEventListener('visibilitychange', update);
      window.removeEventListener('pageshow', update);
      window.removeEventListener('focus', update);
    };
  }, []);

  return visible;
}
